/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import logoSymbolPicture from '@web/src/assets/logo/logo-plain.png?w=40,90;130;200;250;500&format=avif;webp&as=picture'
import logoSymbolChristmasPicture from '@web/src/assets/logo/logo-plain-christmas.webp?w=40,90;130;200;250;500&format=avif;webp&as=picture'
import logoSymbolHalloweenPicture from '@web/src/assets/logo/logo-plain-halloween.webp?w=40,90;130;200;250;500&format=avif;webp&as=picture'
import logoSymbolLightPicture from '@web/src/assets/logo/logo-plain-light.png?w=40,90;130;200;250;500&format=avif;webp&as=picture'
import logoStackedPicture from '@web/src/assets/logo/logo-stacked.webp?w=90;130;200;250;500&format=avif;webp&as=picture'
import logoStackedChristmasPicture from '@web/src/assets/logo/logo-stacked-christmas.png?w=90;130;200;250;500&format=avif;webp&as=picture'
import logoStackedHalloweenPicture from '@web/src/assets/logo/logo-stacked-halloween.webp?w=90;130;200;250;500&format=avif;webp&as=picture'
import logoStackedLightPicture from '@web/src/assets/logo/logo-stacked-light.webp?w=90;130;200;250;500&format=avif;webp&as=picture'
import { useColorScheme } from '@web/src/contexts/color-scheme-context'
import { useAuth } from '@web/src/hooks/use-auth'

import { type ActiveThemeName, resolveLogoTheme } from './index'

/** vite-imagetools `&as=picture` shape used by the logo components. */
export type ThemeLogoPicture = {
  sources: Record<string, string>
  img: { src: string; w: number; h: number }
}

export type ThemeLogos = {
  full: ThemeLogoPicture
  symbol: ThemeLogoPicture
}

const defaultLogos: ThemeLogos = {
  full: logoStackedPicture,
  symbol: logoSymbolPicture,
}

const lightLogos: ThemeLogos = {
  full: logoStackedLightPicture,
  symbol: logoSymbolLightPicture,
}

const halloweenLogos: ThemeLogos = {
  full: logoStackedHalloweenPicture,
  symbol: logoSymbolHalloweenPicture,
}

const christmasLogos: ThemeLogos = {
  full: logoStackedChristmasPicture,
  symbol: logoSymbolChristmasPicture,
}

/** Every active theme must name its logos. Adding a theme without an entry fails typecheck. */
const themeLogos = {
  dark: defaultLogos,
  christmas: christmasLogos,
  halloween: halloweenLogos,
  'preview-light': lightLogos,
} as const satisfies Record<ActiveThemeName, ThemeLogos>

export const logosForTheme = (themeName: ActiveThemeName): ThemeLogos => themeLogos[themeName] ?? themeLogos.dark

/** Logos for the theme that should be shown in the current session. */
export const useThemeLogos = (): ThemeLogos => {
  const { isAuthenticated } = useAuth()
  const { activeHoliday, themeName } = useColorScheme()

  return logosForTheme(resolveLogoTheme({ themeName, activeHoliday, isAuthenticated }))
}
